import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Container, TextField, Typography, Select, MenuItem, FormControl, Radio, RadioGroup, FormControlLabel, FormHelperText } from '@mui/material';
import { authService } from '../../services/authService';
import { Link, useNavigate } from 'react-router-dom';
import logo from "../../assests/logo.png";
import ellipse1 from "../../assests/Ellipse 1.png";
import ellipse2 from "../../assests/Ellipse 2.png";
import { useTheme, useMediaQuery, Paper, FormLabel, InputAdornment } from "@mui/material";
import InstitutionRegister from './InstitutionRegister';
import useRecaptcha from '../../hooks/useRecaptcha';
import { showSuccess, showError } from '../../hooks/swalAlerts';

const textFieldStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "gray" },
    "&:hover fieldset": { borderColor: "white" },
    "&.Mui-focused fieldset": { borderColor: "white" },
  },
  "& .MuiInputBase-input": { color: "white" },
};

const selectFieldStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "gray" },
    "&:hover fieldset": { borderColor: "white" },
    "&.Mui-focused fieldset": { borderColor: "white" },
  },
  "& .MuiInputBase-input": { color: "white" },
  "& .MuiFormLabel-root": { color: "lightgray" },
};

const Register = () => {
  const { executeRecaptcha } = useRecaptcha();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', adhaar: '', gender: '', college_id: '', department_id: '', employee_id: '', password: '', confirmPassword: '', affiliatedCollegeCode: '', affiliatedCollegeName: '' });
  const [errors, setErrors] = useState({ firstName: '', lastName: '', email: '', phone: '', adhaar: '', gender: '', college_id: '', department_id: '', employee_id: '', password: '', confirmPassword: '', affiliatedCollegeCode: '', affiliatedCollegeName: '' });
  const [userType, setUserType] = useState('Students');
  // const [userType, setUserType] = useState('Institution');
  const [institutions, setInstitutions] = useState([]);
  const [departments, setDepartments] = useState([]);
  // const [courses, setCourses] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState('');
  const isFetched = useRef(false);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!isFetched.current) {
      getInstitutions();
      isFetched.current = true;
    }
  }, []);

  useEffect(() => {
    if (formData.college_id) {
      getDepartments(formData.college_id); // pass college ID
    } else {
      setDepartments([]);
      setFormData(prev => ({ ...prev, department: '', course: '' }));
    }
  }, [formData.college_id]);

  useEffect(() => {
    if (formData.department_id) {
      // getCourses(formData.department_id); // pass department ID
    } else {
      // setCourses([]);
      setFormData(prev => ({ ...prev, course: '' }));
    }
  }, [formData.department_id]);

  const getInstitutions = async () => {
    try {
      const response = await authService.getData('/get-colleges');
      if (response) setInstitutions(response);
    } catch (error) {
      console.error('Failed to fetch institutions', error);
    }
  };

  const getDepartments = async (collegeId) => {
    try {
      const response = await authService.getData(`/get-departments?collegeId=${collegeId}`);
      if (response) setDepartments(response);
    } catch (error) {
      console.error('Failed to fetch departments', error);
    }
  };

  // const getCourses = async (departmentId) => {
  //   try {
  //     const response = await authService.getData(`/get-courses?departmentId=${departmentId}`);
  //     // if (response) setCourses(response);
  //   } catch (error) {
  //     console.error('Failed to fetch courses', error);
  //   }
  // };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^\d{10}$/;
    return re.test(phone);
  };

  const validatePassword = (password) => {
    // At least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return re.test(password);
  };

  const validateAadhaar = (adhaar) => {
    // Aadhaar: 12 digits, not starting with 0 or 1
    const re = /^[2-9]{1}[0-9]{11}$/;
    return re.test(adhaar);
  };

  const checkPasswordStrength = (password) => {
    if (!password) return '';
    if (password.length < 8) return 'Weak (min 8 characters)';
    if (!validatePassword(password)) return 'Medium (needs uppercase, number, special char)';
    return 'Strong';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Check password strength in real-time
    if (name === 'password') {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...errors };

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      isValid = false;
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      isValid = false;
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Phone validation
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
      isValid = false;
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
      isValid = false;
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
      isValid = false;
    }

    // Aadhaar validation
    if (!formData.adhaar) {
      newErrors.adhaar = 'Aadhaar number is required';
      isValid = false;
    } else if (!validateAadhaar(formData.adhaar)) {
      newErrors.adhaar = 'Aadhaar must be 12 digits and not start with 0 or 1';
      isValid = false;
    }

    // Institution validation
    if (!formData.college_id) {
      newErrors.college_id = 'Institution is required';
      isValid = false;
    }

    // Department validation
    // if (!formData.department_id) {
    //   newErrors.department_id = 'Department is required';
    //   isValid = false;
    // }

    // Employee ID/Enrollment validation
    if (!formData.employee_id) {
      newErrors.employee_id = userType === 'Students'
        ? 'Enrollment number is required'
        : 'Employee ID is required';
      isValid = false;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      isValid = false;
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
      isValid = false;
    }

    // Confirm Password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleUserTypeChange = (e) => {
    setUserType(e.target.value);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      adhaar: '',
      gender: '',
      college_id: '',
      department_id: '',
      employee_id: '',
      password: '',
      confirmPassword: '',
      affiliatedCollegeCode: '',
      affiliatedCollegeName: ''
    });
    setErrors({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      adhaar: '',
      gender: '',
      college_id: '',
      department_id: '',
      employee_id: '',
      password: '',
      confirmPassword: '',
      affiliatedCollegeCode: '',
      affiliatedCollegeName: ''
    });
    setPasswordStrength('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const recaptchaValue = await executeRecaptcha('register');
      const response = await authService.postData('/register', { ...formData, userType, recaptchaValue });

      setFormData({
        firstName: '', lastName: '', email: '', phone: '', adhaar: '', gender: '', college_id: '',
        department_id: '', employee_id: '', password: '', confirmPassword: '', affiliatedCollegeCode: '', affiliatedCollegeName: ''
      });
      setErrors({
        firstName: '', lastName: '', email: '', phone: '', adhaar: '', gender: '', college_id: '',
        department_id: '', employee_id: '', password: '', confirmPassword: '', affiliatedCollegeCode: '', affiliatedCollegeName: ''
      });
      setPasswordStrength('');

      showSuccess(
        response?.msg ||
        'Registration submitted successfully. Your details will be reviewed and course access will be assigned accordingly.'
      );
      
      navigate('/');
    } catch (error) {
      console.error('Failed to post data', error);
      const errorMsg = error.response?.data?.msg;

      if (errorMsg?.includes('Email')) {
        setErrors(prev => ({ ...prev, email: errorMsg }));
      } else if (errorMsg?.includes('Enrollment ID')) {
        setErrors(prev => ({ ...prev, employee_id: errorMsg }));
      } else {
        showError(errorMsg || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        bgcolor: "#050A1F",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Background elements */}
      <Box sx={{ position: "absolute", width: "100%", height: "100%", overflow: "hidden" }}>
        <Box
          component="img"
          src={ellipse1}
          alt="Decorative ellipse"
          sx={{
            position: "absolute",
            width: { xs: "150%", sm: "100%", md: "812px" },
            height: "auto",
            top: { xs: "-50%", sm: "-25%", md: 0 },
            right: { xs: "-50%", sm: "-25%", md: 0 },
            maxWidth: "none",
          }}
        />
        <Box component="img" src={ellipse2} alt="Decorative ellipse"
          sx={{
            position: "absolute", width: { xs: "150%", sm: "100%", md: "810px" }, height: "auto",
            bottom: { xs: "-50%", sm: "-25%", md: 0 }, left: { xs: "-50%", sm: "-25%", md: 0 }, maxWidth: "none",
          }} />
      </Box>

      {/* Logo */}
      <Box component="img" src={logo} alt="Second Education Logo"
        sx={{
          position: "absolute", width: { xs: "120px", sm: "150px", md: "225px" },
          height: "auto", top: { xs: "16px", sm: "20px", md: "49px" }, left: { xs: "16px", sm: "20px", md: "102px" }, zIndex: 1,
        }} />

      {/* Registration Form */}
      <Container maxWidth="sm" sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", py: { xs: 4, sm: 6 }, zIndex: 1, }} >
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%", bgcolor: "rgba(45, 52, 71, 0.9)", borderRadius: "12px", border: "2px solid white", }} >
            <Typography variant="h4" align="center" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, color: "white" }}>
              Student Registration
            </Typography>

            {/* User Type Selection */}
            <FormControl component="fieldset" sx={{ width: "100%", mb: 2 }}>
              {/* <FormLabel component="legend" sx={{ color: "white", mb: 1 }}>
              Role
            </FormLabel> */}
              <RadioGroup row={!isMobile} name="userType" value={userType} onChange={handleUserTypeChange} sx={{ justifyContent: "center" }} >
                {/* <FormControlLabel value="Institution" control={<Radio sx={{ color: "white", "&.Mui-checked": { color: "white" } }} />} label="Institution" sx={{ color: "white" }} /> */}
                {/* <FormControlLabel value="Students" control={<Radio sx={{ color: "white", "&.Mui-checked": { color: "white" } }} />} label="Student" sx={{ color: "white" }} /> */}
                {/* <FormControlLabel value="Instructors" control={<Radio sx={{ color: "white", "&.Mui-checked": { color: "white" } }} />} label="Instructor" sx={{ color: "white" }} /> */}
              </RadioGroup>
            </FormControl>

            {userType === 'Institution' && (
              <InstitutionRegister executeRecaptcha={executeRecaptcha} />
            )}
            {(userType === 'Students' || userType === 'Instructors') && (
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                  <TextField label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} error={!!errors.firstName}
                    helperText={errors.firstName} required variant="outlined" fullWidth size="small" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle} />
                  <TextField label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} error={!!errors.lastName}
                    helperText={errors.lastName} required variant="outlined" fullWidth size="small" InputLabelProps={{ sx: { color: "lightgray" } }} sx={textFieldStyle} />
                </Box>

                <TextField label="Email" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} required
                  variant="outlined" fullWidth size="small" margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ color: "lightgray" }}>

                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ sx: { color: "lightgray" } }} sx={{ ...textFieldStyle, mt: 0, mb: 2 }} />

                <TextField
                  label="Phone Number" name="phone" type="tel" value={formData.phone} onChange={handleChange} error={!!errors.phone} helperText={errors.phone} required
                  variant="outlined" fullWidth size="small" margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }}
                  inputProps={{ maxLength: 10 }}
                  sx={{ ...textFieldStyle, mt: 0, mb: 2 }} />

                {/* Gender Field */}
                <FormControl fullWidth size="small" margin="normal" required
                  error={!!errors.gender} sx={{ ...selectFieldStyle, mt: 0, mb: 2 }} >
                  <FormLabel sx={{ color: "lightgray", mb: 0.5 }}>Gender</FormLabel>
                  <Select name="gender" value={formData.gender} onChange={handleChange} displayEmpty sx={{ color: "white" }} >
                    <MenuItem value="">Select Gender</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                  {errors.gender && <FormHelperText error>{errors.gender}</FormHelperText>}
                </FormControl>

                {/* Aadhaar Field */}
                <TextField label="Aadhaar Number" name="adhaar" type="text" value={formData.adhaar} onChange={handleChange}
                  error={!!errors.adhaar} helperText={errors.adhaar} required variant="outlined" fullWidth size="small"
                  margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }} sx={{ ...textFieldStyle, mt: 0, mb: 2 }}
                />

                <FormControl fullWidth size="small" margin="normal" required
                  error={!!errors.college_id} sx={{ ...selectFieldStyle, mt: 0, mb: 2 }} >
                  <FormLabel sx={{ color: "lightgray", mb: 0.5 }}> Institution </FormLabel>
                  <Select name="college_id" value={formData.college_id} onChange={handleChange} displayEmpty sx={{ color: "white" }} >
                    <MenuItem value="">Select Institution</MenuItem>
                    {institutions.map((inst) => (
                      <MenuItem key={inst._id} value={inst._id}>{inst.collegeName}</MenuItem>
                    ))}
                  </Select>
                  {errors.college_id && <FormHelperText error>{errors.college_id}</FormHelperText>}
                </FormControl>

                {/* <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                <FormControl fullWidth size="small" required error={!!errors.department_id} sx={selectFieldStyle} >
                  <FormLabel sx={{ color: "lightgray", mb: 0.5 }}> Department </FormLabel>
                  <Select name="department_id" value={formData.department_id} onChange={handleChange} displayEmpty sx={{ color: "white" }} >
                    <MenuItem value="">Select Department</MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept._id} value={dept._id}>{dept.name}</MenuItem>
                    ))}
                  </Select>
                  {errors.department_id && <FormHelperText error>{errors.department_id}</FormHelperText>}
                </FormControl>
              </Box> */}

                <TextField
                  label={userType === 'Students' ? 'Enrollment Number (Username)' : 'Employement ID'}
                  name="employee_id" value={formData.employee_id} onChange={handleChange} error={!!errors.employee_id}
                  helperText={errors.employee_id} required variant="outlined" fullWidth size="small" margin="normal"
                  InputLabelProps={{ sx: { color: "lightgray" } }} sx={{ ...textFieldStyle, mt: 0, mb: 2 }} />

                {/* <TextField
                label='Affiliated College Name'
                name="affiliatedCollegeName" value={formData.affiliatedCollegeName} onChange={handleChange} error={!!errors.affiliatedCollegeName}
                helperText={errors.affiliatedCollegeName} variant="outlined" fullWidth size="small" margin="normal"
                InputLabelProps={{ sx: { color: "lightgray" } }} sx={{ ...textFieldStyle, mt: 0, mb: 2 }} /> */}

                {/* <TextField
                label='affiliated College Code'
                name="affiliatedCollegeCode" value={formData.affiliatedCollegeCode} onChange={handleChange} error={!!errors.affiliatedCollegeCode}
                helperText={errors.affiliatedCollegeCode} variant="outlined" fullWidth size="small" margin="normal"
                InputLabelProps={{ sx: { color: "lightgray" } }} sx={{ ...textFieldStyle, mt: 0, mb: 2 }} /> */}

                <TextField label="Password" name="password" type="password"
                  value={formData.password} onChange={handleChange} error={!!errors.password}
                  helperText={errors.password || (formData.password ? `Password Strength: ${passwordStrength}` :
                    "")
                  } required variant="outlined" fullWidth size="small" margin="normal" InputLabelProps={{ sx: { color: "lightgray" } }}
                  sx={{
                    ...textFieldStyle, mt: 0, mb: 2,
                    '& .MuiFormHelperText-root': { color: errors.password ? undefined : 'white' }
                  }} />

                <TextField label="Confirm Password" name="confirmPassword" type="password" value={formData.confirmPassword}
                  onChange={handleChange} error={!!errors.confirmPassword} helperText={errors.confirmPassword}
                  required variant="outlined" fullWidth size="small" margin="normal"
                  InputLabelProps={{ sx: { color: "lightgray" } }} sx={{ ...textFieldStyle, mt: 0, mb: 3 }} />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{ bgcolor: "#3366FF", color: "white", py: 1.5, mt: { xs: 2, sm: 0 }, "&:hover": { bgcolor: "#2952CC" } }}
                >
                  {loading ? 'Registering...' : 'Register'}
                </Button>
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" sx={{ color: 'lightgray' }}>
                    Have already an account?{' '}
                    <Link to="/" style={{ color: '#3366FF', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      Login here
                    </Link>
                  </Typography>
                </Box>
              </Box>
            )}

          </Paper>
        </Container>
    </Box>
  );
};

export default Register;
